import { useMemo } from 'react'
import { useJourneyContext } from '../context'
import { AddStepType } from '../types/journey-nodes'
import { RECOMMENDATION_NOTIFICATION_INTEGRATIONS } from '../constants'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { ScopeCache, enclosingBranchingNodes } from '../utils/graph/scope'
import { isBranchingNode } from '../utils/graph/traversal'
import { STEP_CONFIG, IStepAvailability } from '../step-config'

/**
 * Computes the list of user-addable step types based on the current journey
 * configuration, domain capabilities, and graph position.
 *
 * Shared by both edge add-step popovers and placeholder replacement pickers.
 *
 * @param nodeId - The node id used to determine branch scope. For edges this
 *   is the source node; for placeholders it's the placeholder node itself.
 */
export function useAddStepOptions(nodeId: string): AddStepType[] {
    const [state] = useJourneyContext()

    return useMemo(() => {
        const triggerType = state.journey?.configuration?.computed_trigger_type ?? TriggerType.SUBSCRIBED

        const hasItemType = !!state.domain.ecommConfig?.itemType
        const hasRecommendationIntegration = Array.from(
            state.dependencies.integrations?.entries() ?? [],
        ).some(([_id, int]) => RECOMMENDATION_NOTIFICATION_INTEGRATIONS.includes(int.provider) && int.is_active)

        const scopeCache = new ScopeCache()
        const nodeMap = new Map(state.nodes.map((n) => [n.id, n]))
        const sourceNode = nodeMap.get(nodeId)
        const isInsideBranch =
            enclosingBranchingNodes(nodeId, state.nodes, scopeCache).length > 0 ||
            (sourceNode != null && isBranchingNode(sourceNode))

        const options: AddStepType[] = []

        for (const config of Object.values(STEP_CONFIG)) {
            if (config.kind !== 'user') continue

            const avail: IStepAvailability | undefined = config.availability
            if (avail) {
                if (avail.allowInsideBranch === false && isInsideBranch) continue
                if (avail.triggerTypes && !avail.triggerTypes.includes(triggerType)) continue
                if (avail.requiresItemType && !hasItemType) continue
                if (avail.requiresRecommendationIntegration && !hasRecommendationIntegration) continue
            }

            options.push(config.type)
        }

        return options
    }, [state.journey?.id, state.domain, state.dependencies.integrations, state.nodes, nodeId])
}
